/**
 * WHICH TERMINAL TYPE A CATALOG DEVICE PROVISIONS AS.
 *
 * The device's screen type (`appProfile.terminalFormFactor`, set on
 * /inventory/stock/<modelId>/edit) is the ONLY place anyone says a K10 is a
 * dual-screen machine. Everything downstream reads the register's
 * `terminalType`, not the catalog:
 *
 *   catalog terminalFormFactor
 *     → POST /pos/terminals/provision  { terminalType }
 *     → the activation response
 *     → the device's `posTerminalConfig.terminalType`
 *     → `deviceProfile.ts` / `CustomerFacingTab` / `resolvePaymentProvider`,
 *       each of which matches 'android_dual_screen' EXACTLY.
 *
 * So a register created as plain 'pos' is a dual-screen terminal that comes up
 * with no customer display and on the lane payment provider — which is exactly
 * what `CreateRegisterPanel` shipped (it hardcoded 'pos') and what this helper
 * exists to stop recurring.
 *
 * The list mirrors `validTypes` in super-admin-ext2's `pos-terminal-provision`
 * handler. That handler rewrites anything it does not recognise to 'pos'
 * SILENTLY, so a value must be checked HERE, where the caller can still show
 * the operator what is about to happen.
 */
export const PROVISIONABLE_TERMINAL_TYPES = [
  'pos', 'kiosk', 'customer_display',
  'android_dual_screen', 'android_single_screen',
  // 2026-09-18: a kiosk's screen count is part of what it IS. Added to
  // `validTypes` in the same change — a value offered here but missing there is
  // rewritten to 'pos' without a word, which is the failure this file exists for.
  'kiosk_single_screen', 'kiosk_dual_screen',
] as const;

/**
 * The provisioning `terminalType` this form factor maps to, or `null` when it
 * is not one provisioning honours — blank (never answered), `not_applicable`
 * (a catalog answer meaning "this item is never a terminal"), or a free-form
 * value someone typed before the list was closed. The form-factor field is
 * `z.string()` in shared-types precisely so old rows keep parsing, so a stored
 * value can be anything at all.
 *
 * `null` is not an error — it means the CALLER must decide, either by asking
 * the operator or by falling back to 'pos' out loud rather than quietly.
 */
export function provisionTypeFromFormFactor(formFactor?: string | null): string | null {
  if (!formFactor) return null;
  return (PROVISIONABLE_TERMINAL_TYPES as readonly string[]).includes(formFactor) ? formFactor : null;
}

/**
 * Same question asked of a catalog model row. Returns 'pos' when the model
 * says nothing usable — the safe, registerable default, matching what the
 * backend would have done anyway.
 */
export function provisionTypeForModel(
  model?: { appProfile?: { terminalFormFactor?: string } } | null,
): string {
  return provisionTypeFromFormFactor(model?.appProfile?.terminalFormFactor) || 'pos';
}
